import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import type { IconDef } from '../icons/icon';
import { IconComponent } from '../icons/icon-reusable';

export type StatCardTone = 'indigo' | 'amber' | 'green' | 'orange' | 'blue' | 'red';

const TONE_CLASSES: Record<StatCardTone, string> = {
  indigo: 'bg-indigo50 text-indigo600',
  amber: 'bg-amber50 text-amber600',
  green: 'bg-green50 text-green600',
  orange: 'bg-orange50 text-orange600',
  blue: 'bg-blue50 text-blue600',
  red: 'bg-red50 text-red600',
};

/**
 * Metric tile: tinted icon, label, big value, optional caption. Set `clickable`
 * to render it as a button with a trailing chevron that emits `cardClick`.
 */
@Component({
  selector: 'app-stat-card-reusable',
  standalone: true,
  imports: [CommonModule, IconComponent],
  // `title` is an @Input; keep it from leaking a native tooltip onto the host.
  host: { '[attr.title]': 'null', class: 'block' },
  template: `
    <ng-template #body>
      <div class="flex items-start gap-3">
        @if (icon) {
          <!-- Nudged down so the tile sits with the label rather than above it. -->
          <span
            class="mt-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-lg"
            [ngClass]="toneClasses"
          >
            <app-icon-reusable [icon]="icon"  class="mt-1" [size]="20" [useCurrentColor]="true"></app-icon-reusable>
          </span>
        }
        <div class="min-w-0 flex-1">
          <p class="truncate text-sm font-medium text-slate600">{{ title }}</p>
          <p class="mt-1 text-fs22 font-bold leading-tight text-slate900">
            @if (loading) {
              <span class="inline-block h-6 w-10 animate-pulse rounded bg-slate200 align-middle"></span>
            } @else {
              {{ value }}
            }
          </p>
        </div>
        @if (clickable) {
          <svg class="mt-1 shrink-0 text-slate600" width="8" height="14" viewBox="0 0 7 12" fill="none" aria-hidden="true">
            <path d="M1 1L6 6L1 11" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        }
      </div>
      @if (caption) {
        <p class="mt-3 text-xs text-slate500">{{ caption }}</p>
      }
    </ng-template>

    @if (clickable) {
      <button
        type="button"
        class="block h-full w-full cursor-pointer rounded-[10px] border border-slate200 bg-white p-3 shadow-xxsmall text-left transition hover:border-indigo300 hover:shadow-custom focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo300"
        (click)="cardClick.emit()"
      >
        <ng-container [ngTemplateOutlet]="body"></ng-container>
      </button>
    } @else {
      <article class="h-full rounded-[10px] border border-slate200 bg-white p-3 shadow-xxsmall">
        <ng-container [ngTemplateOutlet]="body"></ng-container>
      </article>
    }
  `,
})
export class StatCardReusableComponent {
  @Input() title = '';
  @Input() value: string | number | null = null;
  @Input() caption = '';
  @Input() icon: IconDef | null = null;
  @Input() tone: StatCardTone = 'indigo';
  @Input() clickable = false;
  @Input() loading = false;

  @Output() cardClick = new EventEmitter<void>();

  protected get toneClasses(): string {
    return TONE_CLASSES[this.tone];
  }
}
